import React, { Component } from "react";
import { connect } from "react-redux";
import { WidthProvider, Responsive } from "react-grid-layout";
import * as _ from "lodash";
import Axios from "axios";
import jwt from "jsonwebtoken";
import moment from "moment";
import { message } from "antd";
import BoardDataTableContainer from "./BoardDataTableContainer";
import LoadingAnimation from "../../../components/common/LoadingAnimation";
import WidgetGlobalFilters from "../Filters/WidgetGlobalFilters";
import { returnDataArrayObject } from "../../../utils/arrayHelper";
import BoardChartContainer from "./BoardChartContainer";
import { exportRowsToCsv, exportChartToCsv } from "../../../utils/widgetExport";
import { resolveWidgetDataFilters } from "../../../utils/widgetScope";
import { buildDatePeriods, periodName, readDateDisplay } from "../../../utils/dateFilterDisplay";
import "../../../styles/boardWidgets.css";

const ResponsiveReactGridLayout = WidthProvider(Responsive);

class ReportWidjets extends Component {
  /* ── SAME GRID UNITS AS THE EDITOR CANVAS ──
     The board was laid out against 12 columns of 30px rows (Editor/index.js
     defaultProps, breakpoints lg:0 / cols lg:12). Reading the same saved x/y/w/h back
     against 40px rows drew every widget a third taller than it was designed to be, and
     the 6-column md/sm entries below reinterpreted a 12-column layout at half the
     columns, so widgets that sat side by side wrapped into a stack.
     (Was: rowHeight 40, cols { lg: 12, md: 6, sm: 6, xs: 4, xxs: 2 }.) */
  static defaultProps = {
    className: "reportview-layout",
    rowHeight: 30,
    cols: { lg: 12 },
    /* verticalCompact: false is left out on purpose - it out-ranks compactType inside
       RGL, so it would cancel the "vertical" compaction the editor saves with. */
    preventCollision: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      layouts: null,
      reportJoinData: [],
      fieldsToShow: [],
      updateCount: 0,
      // Safety valve for the loading overlay — see isLoadingWidgets().
      loaderExpired: false,
      /* A refetch is in flight - Apply or Reset on a global filter, or Refresh. The
         first load has its own overlay (isLoadingWidgets); this covers every one
         after it, when the widgets already hold rows. */
      isRefetching: false,
    };
  }

  /* ── THE OVERLAY ON A REFETCH ────────────────────────────────────────────────
     Apply and Reset both re-query every resource, which takes as long as the slowest
     endpoint. Without this the table went on showing the OLD rows with nothing to say
     a request was running, so a reader could not tell a slow filter from one that had
     done nothing at all - and pressed Apply again.

     Counted rather than a plain flag: a dashboard refetches every widget and the
     overlay has to stay up until the last one lands. The timeout is the same safety
     valve the first-load spinner has - a branch that neither resolves nor rejects (a
     JWT resource with no private key, say) must not strand it. */
  beginRefetch = (count) => {
    clearTimeout(this.refetchTimeout);
    this.pendingFetches = count;
    this.setState({ isRefetching: true });
    this.refetchTimeout = setTimeout(() => this.setState({ isRefetching: false }), 20000);
  };

  settleFetch = () => {
    this.pendingFetches = Math.max(0, (this.pendingFetches || 0) - 1);
    if (this.pendingFetches === 0) {
      clearTimeout(this.refetchTimeout);
      this.setState({ isRefetching: false });
    }
  };

  // `layouts` stays null until the first resource's data arrives, so the page sits
  // empty while the widgets load. This reports that gap so one spinner can cover it
  // instead of a blank canvas followed by per-widget spinners.
  isLoadingWidgets = () => {
    const resources = (this.props.board && this.props.board.resources) || [];
    // Nothing to wait for on an empty board.
    if (!resources.length || this.state.loaderExpired) return false;
    const { layouts } = this.state;
    if (!layouts || !layouts.length) return true;
    return !layouts.every((el) => el && el.reportData && el.reportData.rawData && el.reportData.fieldsToShow);
  };

  /* Global filter values each widget's data was fetched with, by widget - see
     componentDidUpdate. Seeded from the board as loaded. */
  fetchedGlobalFilters = {};

  globalFilterKeyOf = (resource) => resource?.connectionData?.uid ?? resource?.connectionData?.id ?? resource?.id;

  componentDidMount() {
    (this.props.board?.resources || []).forEach((r) => {
      this.fetchedGlobalFilters[this.globalFilterKeyOf(r)] = JSON.stringify(r?.customFilters || []);
    });

    /* Straight away, so the header's Download and Email start disabled rather than
       becoming disabled a tick after the page draws. */
    this.reportBusyState();

    // Never let one resource that fails to return keep the overlay up over the
    // widgets that did load — after this the report shows whatever is ready.
    this.loaderTimeout = setTimeout(() => this.setState({ loaderExpired: true }), 20000);

    if (this.props.board && this.props.board.resources) {
      this.fetchReport();
    }

    if (this.props.board && this.props.board.properties) {
      const { properties } = this.props.board;

      if (properties.autorefresh) {
        let timeout = properties.refreshInterval || "30s";

        // this.refreshInterval = setInterval(() => {
        //     fetchReportData(resource, null)
        // }, 1000*30);
        // return () => clearInterval(interval);

        switch (timeout) {
          case "30s": {
            this.refreshInterval = setInterval(() => {
              this.fetchReport();
            }, 1000 * 30);
          }
          case "1m": {
            this.refreshInterval = setInterval(() => {
              this.fetchReport();
            }, 1000 * 60);
          }
          case "5m": {
            this.refreshInterval = setInterval(
              () => {
                this.fetchReport();
              },
              1000 * 60 * 5,
            );
          }
          case "10m": {
            this.refreshInterval = setInterval(
              () => {
                this.fetchReport();
              },
              1000 * 60 * 10,
            );
          }
          case "1h": {
            this.refreshInterval = setInterval(
              () => {
                this.fetchReport();
              },
              1000 * 60 * 60,
            );
          }
          case "3h": {
            this.refreshInterval = setInterval(
              () => {
                this.fetchReport();
              },
              1000 * 60 * 60 * 30,
            );
          }
          default:
          // code block
        }
      }
    }
  }

  /* What identifies this widget - the name on its header bar, and the name at the head of
     anything exported from it.

     A board holding ONE widget is identified by the board: that is what the page header
     says, and the widget draws no header of its own to say anything else, so a file off it
     is "17/02/2023 first report", not the name of the resource behind it. Reports are
     always that case; so are the dashboards that were built with a single widget. A board
     with several widgets needs each one named separately, and there the widget's own name
     is the only thing telling them apart. */
  widgetTitleFor = (resource) => {
    if (this.isSingleWidgetBoard()) return this.props.board?.name || "";
    return resource?.widgetName ? resource.widgetName : resource?.connectionData?.name || "";
  };

  isReportBoard = () => this.props.board?.configDetails?.boardType === "reports";

  /* ── ONE WIDGET, NO HEADER, WHOLE PAGE ──
     What matters here is how many widgets the board holds, not what it is called. A
     report is single by definition, but plenty of one-widget boards were built as
     dashboards before the report type existed, and they were being drawn the long way
     round: a header bar repeating a name the page header already carries, and the
     widget parked at whatever x/y/w/h it happened to be drawn at, with the rest of the
     page empty around it.

     Either way there is nothing to tell apart and nothing to lay out, so the widget is
     given the whole area (see render) and its header bar is dropped. Two or more
     widgets and everything below stays exactly as it was - names, buttons, saved
     positions. This is the view page's rule only; the editor canvas and the scheduled
     report page keep their own. */
  isSingleWidgetBoard = () => this.isReportBoard() || (this.props.board?.resources || []).length === 1;

  /* Which action buttons this widget's header shows.
     On a single-widget board there is no header to put them in - the board toolbar in
     the page header carries Export and Full screen for it instead (see Dashboard's
     soleWidgetProps).

     `csvdownload` is passed through as saved, but it no longer decides whether the widget
     can be downloaded at all - only whether CSV is one of the formats offered. The
     header's menu carries PDF, PNG and XLS whatever the board says, because withholding
     every way of taking the numbers away is not a display preference. Full screen is one,
     so it stays as saved. */
  widgetPropertiesFor = (resource) => {
    if (this.isSingleWidgetBoard()) return {};
    const properties = resource?.properties || {};
    return { csvdownload: Boolean(properties.csvdownload), fullscreen: Boolean(properties.fullscreen) };
  };

  /* ── THE LONE WIDGET'S DOWNLOAD MENU ──
     On a one-widget board the widget draws no header, so its menu sits in the page header
     instead - which is a different component, one level up. The widget hands its export
     routine here as it renders and the board header reaches it through the same ref it
     already uses for downloadAllWidgets. Nothing is registered on a multi-widget board:
     there each widget keeps its own menu, and a board-level one could not say which
     widget it meant. */
  registerSoleExport = (fn) => {
    this.soleWidgetExport = fn;
  };

  /* The lone widget's row count, on its way to the page header - see the onRowCount prop
     below. Only forwarded when it changes, so a widget re-rendering for any other reason
     does not set state on the page. */
  reportSoleRowCount = (count) => {
    if (count === this.lastRowCount) return;
    this.lastRowCount = count;
    if (typeof this.props.onRowCountChange === "function") this.props.onRowCountChange(count);
  };

  exportSoleWidget = (format) => {
    if (typeof this.soleWidgetExport !== "function") return false;
    return this.soleWidgetExport(format);
  };

  /* ── THE EMAIL ATTACHMENTS ──
     Every table widget hands up a routine that renders its rows as a PDF, and the header
     calls all of them when the dialog's Send is pressed.

     Unlike the download menu above, this is NOT limited to a one-widget board. Download
     had to be, because a page-level button that writes four files at once is not what
     anyone presses it for - but an email carries a list of attachments natively, so a
     four-widget dashboard becomes one message with four PDFs. That is the whole reason
     Email can be offered where Download could not.

     Keyed by widget so a re-render replaces a builder rather than adding a second copy of
     it, which would have attached every table twice. */
  emailAttachmentBuilders = new Map();

  registerEmailAttachment = (key) => (fn) => {
    if (typeof fn === "function") this.emailAttachmentBuilders.set(key, fn);
    else this.emailAttachmentBuilders.delete(key);
  };

  /* Every widget's PDF, in one array, for the dialog to attach.

     Built in parallel - each is an independent jsPDF render, and a dashboard of six
     tables done one after another keeps the user waiting on the sum of them.

     Both kinds of widget take part: a table renders its rows (buildRowsPdfAttachment), a
     chart photographs its node (buildNodePdfAttachment). So a board holding one of each
     sends a single email with two PDFs.

     A widget that yields nothing - no rows, or a chart that has not rendered - is dropped
     rather than sent as an empty file. */
  buildEmailAttachments = async () => {
    const builders = Array.from(this.emailAttachmentBuilders.values());
    if (!builders.length) return [];
    const built = await Promise.all(builders.map((build) => build()));
    return built.filter(Boolean);
  };

  /* Board-level Export. The board header had this button long before anything answered
     it - handleBannerChange has no "export" branch - so it rendered and did nothing.
     The rows only exist here, in `layouts`, which is why the implementation lives here
     and Dashboard calls it through a ref rather than the other way round.

     A report has one widget so this writes one file; a dashboard whose board-level
     download was switched on before it became a per-widget property writes one per
     widget, which is what the old button was labelled to do. */
  downloadAllWidgets = () => {
    const layouts = this.state.layouts || [];
    let written = 0;

    layouts.forEach((el) => {
      const data = el?.reportData;
      if (!data || !data.rawData) return;

      const rows = data.groupedReportData?.length ? data.groupedReportData : data.rawData?.data;

      const ok =
        el.widgetType === "chart"
          ? exportChartToCsv({
              rows,
              groupByField: el.chartConfig?.groupBy?.fieldName,
              valueField: el.chartConfig?.fieldName?.fieldName,
              widgetName: data.widgetName,
            })
          : exportRowsToCsv({ rows, fields: data.fieldsToShow, widgetName: data.widgetName });

      if (ok) written += 1;
    });

    if (!written) message.warning("There is no data to download yet");
    return written;
  };

  componentWillUnmount() {
    // Clear the interval when the component is unmounted to avoid memory leaks
    clearInterval(this.refreshInterval);
    clearTimeout(this.loaderTimeout);
    clearTimeout(this.refetchTimeout);
  }

  componentDidUpdate(prevProps, prevState) {
    // if (
    //   this.props.selectedResource &&
    //   this.props.selectedResource.customFilters &&
    //   JSON.stringify(prevProps.selectedResource) !== JSON.stringify(this.props.selectedResource)
    // ) {
    //   this.fetchReport();
    // }
    /* Refetch only when the GLOBAL filter values change - they go into the request.
       Data filters (a column's funnel) trim rows already loaded and need no request;
       comparing the whole resource refetched on every data-filter Apply too, since
       customFilters is always present (an empty array still passes the check). */
    /* Compared with what each widget was last FETCHED with, not with the previous
       prop: a data-filter Apply sets selectedResource to null and back, and that
       round trip must not read as a global-filter change. */
    const selected = this.props.selectedResource;
    if (selected) {
      const key = this.globalFilterKeyOf(selected);
      const current = JSON.stringify(selected.customFilters || []);
      if (this.fetchedGlobalFilters[key] !== current) {
        this.fetchedGlobalFilters[key] = current;
        this.fetchReport();
      }
    }

    // see reportBusyState - the header's Download and Email wait on this
    this.reportBusyState();
  }

  fetchReport = async () => {
    if (this.props.board && this.props.board.resources) {
      /* Every widget needs its own grid key. `j?.layout?.i || 0` gave every widget with
         no saved id the same key 0 - and the grid draws one item per key, so the second
         one vanished ("only the Line Chart is displayed"). Missing or repeated ids now get
         a unique one from the widget's position in the list; saved ids are kept as they are. */
      const gridKeys = this.gridKeysFor(this.props.board.resources);
      let layoutData = this.props.board.resources.map(function (j, key) {
        return {
          // i: j?.layout?.i || 0,
          i: gridKeys[key],
          x: j?.layout?.x || 0,
          y: j?.layout?.y || 0,
          w: j?.layout?.w || 4,
          h: j?.layout?.h || 6,
          widgetType: j.charts && Boolean(Object.values(j.charts).length) ? "chart" : "table",
          chartConfig: j?.charts || null,
          resourceId: j?.resourceId || null,
        };
      });
      /* Only a REFETCH gets this - on first load `layouts` is still null and the
         full-page spinner covers it. */
      if (this.state.layouts && this.state.layouts.length) {
        this.beginRefetch(this.props.board.resources.length);
      }

      this.props.board.resources &&
        this.props.board.resources.forEach(async (resource, idx) => {
          if (
            resource.connectionData &&
            resource.connectionData &&
            resource.connectionData.sources &&
            resource.connectionData.sources.length > 0
          ) {
            layoutData[idx].reportData = {
              rawData: { data: resource.reportData },
              fieldsToShow: resource.availableFields,
              aggregation: resource.aggregation ? resource.aggregation : null,
              aggRowBuilders: resource.aggRowBuilders ? resource.aggRowBuilders : null,
              aggGroupBuilders: resource.aggGroupBuilders ? resource.aggGroupBuilders : null,
              groupedReportData: resource.groupedReportData ? resource.groupedReportData : null,
              widgetName: this.widgetTitleFor(resource),
              // per-widget display properties (download / full screen) - board-level on a report
              widgetProperties: this.widgetPropertiesFor(resource),
              connectionData: resource.connectionData,
            };
            this.setState({
              fieldsToShow: [...this.state.fieldsToShow, resource.availableFields],
              layouts: layoutData,
            });
            this.settleFetch();
          } else {
            if (
              resource.connectionData &&
              resource.connectionData &&
              resource.connectionData.resoueceType === "Rest API"
            ) {
              // const response = await axios.get(resource.connectionData.baseUrl);
              const requestValues = resource.connectionData.connectionData.requestValues;
              const authInfo = resource.connectionData.connectionData.authInfo;
              const customFilters = resource.customFilters ? resource.customFilters : null;

              let urlParamSring = "";
              if (requestValues.urlParams.length > 0) {
                urlParamSring = urlParamSring + "?";
                for (let i = 0; i < requestValues.urlParams.length; i++) {
                  if (requestValues.urlParams[i]["key"] !== "" && requestValues.urlParams[i]["value"] !== "") {
                    urlParamSring =
                      urlParamSring +
                      requestValues.urlParams[i]["key"] +
                      "=" +
                      requestValues.urlParams[i]["value"] +
                      "&";
                  }
                }
                urlParamSring.slice(0, -1);
              }

              let headerObj = {};
              if (requestValues.requestHeaders.length > 0) {
                for (let i = 0; i < requestValues.requestHeaders.length; i++) {
                  if (
                    requestValues.requestHeaders[i]["key"] !== "" &&
                    requestValues.requestHeaders[i]["value"] !== ""
                  ) {
                    headerObj[requestValues.requestHeaders[i]["key"]] = requestValues.requestHeaders[i]["value"];
                  }
                }
              }

              let bodyParamObj = {};
              // if (requestValues.authParams) {
              //     bodyParamObj = Object.assign({}, authParams);
              // }
              if (requestValues.bodyParams.length > 0) {
                for (let i = 0; i < requestValues.bodyParams.length; i++) {
                  if (requestValues.bodyParams[i]["key"] !== "" && requestValues.bodyParams[i]["value"] !== "") {
                    bodyParamObj[requestValues.bodyParams[i]["key"]] = requestValues.bodyParams[i]["value"];
                  } else if (
                    requestValues.bodyParams[i]["key"] !== "" &&
                    requestValues.bodyParams[i]["dataType"] === "date"
                  ) {
                    if (requestValues.bodyParams[i]["dateConfig"]) {
                      let dateCf = requestValues.bodyParams[i]["dateConfig"];
                      if (dateCf.dateOption === "daysBefore") {
                        let dateVal = moment
                          .utc()
                          .subtract(dateCf.timeUnits, "day")
                          .startOf("day")
                          .format(dateCf.dateFormat);
                        bodyParamObj[requestValues.bodyParams[i]["key"]] = dateVal;
                      } else if (dateCf.dateOption === "today") {
                        let dateVal = moment().format(dateCf.dateFormat);
                        bodyParamObj[requestValues.bodyParams[i]["key"]] = dateVal;
                      }
                    }
                  }
                }
              }

              if (customFilters && customFilters.length > 0) {
                customFilters.forEach((element) => {
                  if (element.parameterName !== "") {
                    if (element.parameterType === "query") {
                      if (urlParamSring === "") {
                        urlParamSring = urlParamSring + "?";
                      }
                      urlParamSring = urlParamSring + element.parameterName + "=" + element.value + "&";
                    } else {
                      if (element.value) {
                        bodyParamObj[element.parameterName] = element.value;
                      }
                    }
                  }
                });
              }
              // // await Axios.get(resource.connectionData.baseUrl)

              if (authInfo.method === "jwt") {
                const timestamp = Math.floor(Date.now() / 1000) + 1000;
                const privateKey = authInfo.parameters.privateKey;
                let userToken = localStorage.getItem("accessToken");
                var payload = {
                  aud: authInfo.parameters.aud,
                  exp: timestamp,
                  iss: authInfo.parameters.iss,
                  userToken: userToken,
                };
                var signOptions = {
                  algorithm: "RS256",
                };

                var privateKeyVal = privateKey ? privateKey.replace(/\\n/g, "\n") : null;

                if (privateKeyVal) {
                  try {
                    var token = jwt.sign(payload, privateKeyVal, signOptions);
                    // setApiTestStatus("success")
                    let httpConfig = null;

                    if (
                      requestValues.bodyParams.length > 0 &&
                      resource.connectionData.connectionData.requestValues.httpMethod === "post"
                    ) {
                      httpConfig = {
                        method: "post",
                        // url: resource.connectionData.connectionData.baseUrl + urlParamSring,
                        headers: {
                          "Access-Control-Allow-Origin": "*",
                          "Content-Type": "application/json",
                          Authorization: `Bearer ` + token,
                        },
                        body: JSON.stringify(bodyParamObj),
                      };
                      await fetch(resource.connectionData.connectionData.baseUrl + urlParamSring, httpConfig)
                        .then((data) => data.json())
                        .then((response) => {
                          if (response) {
                            layoutData[idx].reportData = {
                              rawData: response
                                ? returnDataArrayObject(response, null, {
                                    matchBy: resource?.dateFilterDisplay?.matchBy,
                                  })
                                : null,
                              fieldsToShow: resource.availableFields ? resource.availableFields : null,
                              aggregation: resource.aggregation ? resource.aggregation : null,
                              aggRowBuilders: resource.aggRowBuilders ? resource.aggRowBuilders : null,
                              aggGroupBuilders: resource.aggGroupBuilders ? resource.aggGroupBuilders : null,
                              groupedReportData:
                                resource.groupedReportData && Boolean(resource.groupedReportData.length)
                                  ? resource.groupedReportData
                                  : null,
                              widgetName: this.widgetTitleFor(resource),
                              // per-widget display properties (download / full screen) - board-level on a report
                              widgetProperties: this.widgetPropertiesFor(resource),
                              connectionData: resource.connectionData,
                              drillDowns: resource.drillDowns ? resource.drillDowns : null,
                            };
                            this.setState({
                              fieldsToShow: [...this.state.fieldsToShow, resource.availableFields],
                              layouts: layoutData,
                            });
                          }
                          this.settleFetch();
                        })

                        .catch((error) => {
                          // setReportDataStatus("error")
                          // setReportDataObject(null)
                          console.log(error);
                          this.settleFetch();
                        });
                    }
                  } catch (e) {
                    console.log("JWT authPsign error=====>>", e);
                  }
                }
              } else {
                let httpConfig = {
                  method: resource.connectionData.connectionData.requestValues.httpMethod,
                  url: resource.connectionData.connectionData.baseUrl + urlParamSring,
                };
                if (requestValues.bodyParams.length > 0) {
                  httpConfig.body = bodyParamObj;
                }
                if (requestValues.requestHeaders.length > 0) {
                  httpConfig.headers = headerObj;
                }

                await Axios(httpConfig)
                  .then(async (response) => {
                    // rowDataArr.push(returnDataArrayObject(response.data, null, { matchBy: resource?.dateFilterDisplay?.matchBy })
                    layoutData[idx].reportData = {
                      rawData: returnDataArrayObject(response.data, null, {
                        matchBy: resource?.dateFilterDisplay?.matchBy,
                      }),
                      fieldsToShow: resource.availableFields,
                      aggregation: resource.aggregation ? resource.aggregation : null,
                      aggRowBuilders: resource.aggRowBuilders ? resource.aggRowBuilders : null,
                      aggGroupBuilders: resource.aggGroupBuilders ? resource.aggGroupBuilders : null,
                      groupedReportData: resource.groupedReportData ? resource.groupedReportData : null,
                      widgetName: this.widgetTitleFor(resource),
                      // per-widget display properties (download / full screen) - board-level on a report
                      widgetProperties: this.widgetPropertiesFor(resource),
                      connectionData: resource.connectionData,
                      drillDowns: resource.drillDowns ? resource.drillDowns : null,
                    };
                    this.setState({
                      fieldsToShow: [...this.state.fieldsToShow, resource.availableFields],
                      layouts: layoutData,
                    });
                    this.settleFetch();

                    // setReportDataStatus("success")
                    // setReportDataObject(response.data ? response.data : response)
                  })
                  .catch((error) => {
                    // setReportDataStatus("error")
                    // setReportDataObject(null)
                    console.log(error);
                    this.settleFetch();
                  });
              }
            }
          }
        });
    }
  };

  /* One unique grid key per widget, in board order - see fetchReport. Saved ids are
     kept; a missing or repeated one gets a key from the widget's position. */
  gridKeysFor = (resources) => {
    const seen = new Set();
    return (resources || []).map((j, idx) => {
      const saved = j?.layout?.i;
      let key = saved !== undefined && saved !== null && saved !== "" ? String(saved) : `w${idx}`;
      if (seen.has(key)) key = `${key}-${idx}`;
      seen.add(key);
      return key;
    });
  };

  fecthMultiResReport = async (resource, index) => {
    let dataList = [];
    const gridKeys = this.gridKeysFor(this.props.board.resources);
    resource.connectionData.sources.forEach((src) => {
      let layoutData = this.props.board.resources.map(function (j, key) {
        return {
          // i: j.layout && j.layout.i ? j.layout.i : 0,
          i: gridKeys[key],
          x: j.layout && j.layout.x ? j.layout.x : 0,
          y: j.layout ? j.layout.y : 0,
          w: j.layout ? j.layout.w : 4,
          h: j.layout ? j.layout.h : 6,
          widgetType: j.charts && Boolean(Object.values(j.charts).length) ? "chart" : "table",
        };
      });

      layoutData[index].reportData = {
        rawData: resource.reportData,
        fieldsToShow: resource.availableFields ? resource.availableFields : null,
        aggregation: resource.aggregation ? resource.aggregation : null,
        aggRowBuilders: resource.aggRowBuilders ? resource.aggRowBuilders : null,
        aggGroupBuilders: resource.aggGroupBuilders ? resource.aggGroupBuilders : null,
        groupedReportData: resource.groupedReportData ? resource.groupedReportData : null,
        widgetName: this.widgetTitleFor(resource),
        // per-widget display properties (download / full screen) - board-level on a report
        widgetProperties: this.widgetPropertiesFor(resource),
        connectionData: resource.connectionData,
        drillDowns: resource.drillDowns ? resource.drillDowns : null,
      };

      this.setState({
        reportJoinData: [
          ...this.state.reportJoinData,
          {
            reportData: { data: resource.reportData },
            resourceId: src.id,
            name: src.name,
          },
        ],
        fieldsToShow: [...this.state.fieldsToShow, resource.availableFields],
        layouts: layoutData,
      });
    });
  };

  fetchReportData = async () => {
    let rowDataArr = [];
  };

  /* Data filters belong to the resource they were configured on - see
     utils/widgetScope.js for the rule and its tests. */
  dataFiltersFor(el) {
    return resolveWidgetDataFilters(this.props.selectedResource, el?.reportData?.connectionData?.id);
  }

  /* The board resource this widget was drawn from. Global filters are per-resource,
     so the band above a widget has to edit that resource's own parameters - matched
     on the connection id, the same key onApplyFilterToResource swaps on. */
  /* Response key -> the name the author gave that period. Matched by ORDER - the
     first response array is the first date range - which is the same positional rule
     the filters pair by, so the chart and the table cannot label them differently. */
  periodLabelsFor(el) {
    const resource = this.resourceFor(el);
    const keys = el?.reportData?.rawData?.periodKeys || [];
    if (!resource || !keys.length) return undefined;

    const display = readDateDisplay(resource);
    const periods = buildDatePeriods(resource.customFilters || []);
    const labels = {};
    keys.forEach((key, i) => {
      labels[key] = periodName(display, i, periods.length > 1);
    });
    return labels;
  }

  resourceFor(el) {
    const id = el?.reportData?.connectionData?.id;
    if (!id) return null;
    return (this.props.board?.resources || []).find((r) => (r?.connectionData?.id ?? r?.id) === id) || null;
  }

  /* The resource the column funnels edit: the board's, with the data filters as
     last APPLIED on this page when this widget has been filtered. resourceFor alone
     gave the board as loaded, so every funnel Apply started again from the saved
     values - applying Name after ID dropped the ID just applied. */
  filterStateFor(el) {
    const base = this.resourceFor(el);
    if (!base) return base;
    const applied = resolveWidgetDataFilters(this.props.selectedResource, el?.reportData?.connectionData?.id);
    return applied ? { ...base, dataFilters: applied } : base;
  }

  /* The widget itself, with no cell around it. Split out of generateDOM so a
     single-widget board can drop it straight into a full-height box instead of a grid
     cell - both paths draw exactly the same widget. */
  /* ── ONE WIDGET'S OWN FILTER BAND ────────────────────────────────────────────
     Built here and handed DOWN into the widget, which renders it directly under its
     header - so a reader sees the controls attached to the thing they drive.

     Returns null on a one-widget board: there the band belongs above the widget, in
     the page toolbar. See renderFilterBands. */
  filtersNodeFor(el) {
    if (!this.props.onApplyGlobalFilter || this.isSingleWidgetBoard()) return null;

    const resource = this.resourceFor(el);
    if (!resource || !(resource.customFilters || []).length) return null;

    return (
      <WidgetGlobalFilters
        resource={resource}
        /* The widget's own header is right above this now and already carries its
           name, so the band does not repeat it. */
        widgetName={null}
        inWidget
        onApplyFilter={this.props.onApplyGlobalFilter}
      />
    );
  }

  /* ── WHERE THE BAND GOES, AND WHY IT DEPENDS ON THE BOARD ────────────────────
     A REPORT (one widget) puts it in a toolbar ABOVE, because the widget IS the page
     and draws no header of its own - there is nothing for the band to attach to, and
     nothing to disambiguate.

     A DASHBOARD puts each band INSIDE its own widget, under that widget's header.
     With several widgets stacked in one toolbar a reader had to match a band to a
     chart by name, which is work the layout should be doing.

     That is the host app's own arrangement: witmeg-reporting-embed-v2 renders
     `filtersNode` into `.report-page-toolbar` when isPageLayout and uses an in-widget
     band for a dashboard widget.

     The in-widget placement was tried once before and abandoned, because
     `.board-widget-single > div` styles every direct div child as a flex column that
     grows - the band rendered sideways and clipped. That rule is scoped to
     `.board-widget-single`, which is the ONE-widget case, and that case still draws
     its band up here. The two arrangements avoid each other by construction. */
  renderFilterBands() {
    if (!this.props.onApplyGlobalFilter || !this.isSingleWidgetBoard()) return null;

    const el = (this.state.layouts || [])[0];
    const resource = el ? this.resourceFor(el) : null;
    if (!resource || !(resource.customFilters || []).length) return null;

    return (
      <div className="wgf-toolbar">
        <WidgetGlobalFilters resource={resource} widgetName={null} onApplyFilter={this.props.onApplyGlobalFilter} />
      </div>
    );
  }

  renderWidgetBody(el) {
    return (
      <>
        {el.reportData && el.reportData.rawData && el.reportData.fieldsToShow ? (
          <>
            {el.widgetType === "table" ? (
              <BoardDataTableContainer
                reportData={
                  el.reportData?.groupedReportData && Boolean(el.reportData?.groupedReportData.length)
                    ? { data: el.reportData.groupedReportData }
                    : el.reportData.rawData
                }
                dataFilters={this.dataFiltersFor(el)}
                selectedResource={this.props.selectedResource}
                /* Column filters live in the table header on the board view, the way
                   the host app draws them. Only passed here - the editor's ReportGrid
                   gets none of this and keeps its plain headers. */
                // filterResource={this.resourceFor(el)}
                filterResource={this.filterStateFor(el)}
                /* This widget's own global filters, drawn under its header on a
                   dashboard - null on a report, where they sit above instead. */
                filtersNode={this.filtersNodeFor(el)}
                // for "Clear filters" on an empty table, which clears the global ones too
                onApplyGlobalFilter={this.props.onApplyGlobalFilter}
                onApplyDataFilter={this.props.onApplyDataFilter}
                onResetDataFilter={this.props.onResetDataFilter}
                availableFields={el.reportData.fieldsToShow}
                aggregation={el.reportData.aggregation}
                aggGroupBuilders={el.reportData.aggGroupBuilders}
                aggRowBuilders={el.reportData.aggRowBuilders}
                widgetName={el.reportData.widgetName}
                widgetProperties={el.reportData.widgetProperties}
                /* on a one-widget board the page header already says this - see
                   isSingleWidgetBoard. The name is still passed through: it is the
                   CSV file name. */
                hideHeader={this.isSingleWidgetBoard()}
                /* the board name heads every exported file; the registration is how the
                   page header reaches this widget's download menu - see registerSoleExport */
                boardName={this.props.board?.name}
                onRegisterExport={this.isSingleWidgetBoard() ? this.registerSoleExport : undefined}
                /* EVERY widget registers one - tables here, charts below - on a dashboard
                   as well as a report. An email takes a list of attachments, so a board of
                   four widgets becomes one message carrying four PDFs. */
                onRegisterEmailAttachment={this.registerEmailAttachment(el.i ?? el.resourceId)}
                /* Only on a one-widget board: there the widget draws no header of its own,
                   so its row count goes beside the page title instead. With several widgets
                   each keeps its count in its own header, where it says which table it
                   belongs to. */
                onRowCount={this.isSingleWidgetBoard() ? this.reportSoleRowCount : undefined}
                createdAt={this.props.board.createdAt}
                updatedAt={this.props.board.updatedAt}
                connectionData={el.reportData.connectionData}
                drillDowns={el.reportData.drillDowns}
                resource={el}
              />
            ) : (
              <BoardChartContainer
                reportData={el.reportData.rawData}
                dataFilters={this.dataFiltersFor(el)}
                selectedResource={this.props.selectedResource}
                /* This widget's own resource, for the global filter values an export
                   names in its heading - the table gets the same thing. */
                filterResource={this.resourceFor(el)}
                // as the table above - the widget's own filter band, dashboards only
                filtersNode={this.filtersNodeFor(el)}
                /* Comparison context - a chart draws one line per period when the
                   rows were matched on a key. See BoardChartContainer. */
                periodKeys={el.reportData.rawData?.periodKeys}
                periodLabels={this.periodLabelsFor(el)}
                matchBy={this.resourceFor(el)?.dateFilterDisplay?.matchBy}
                availableFields={el.reportData.fieldsToShow}
                aggregation={el.reportData.aggregation}
                aggRowBuilders={el.reportData.aggRowBuilders}
                widgetName={el.reportData.widgetName}
                widgetProperties={el.reportData.widgetProperties}
                // same as the table above - a lone widget's name is on the page header already
                hideHeader={this.isSingleWidgetBoard()}
                /* the board name heads every exported file; the registration is how the
                   page header reaches this widget's download menu - see registerSoleExport */
                boardName={this.props.board?.name}
                onRegisterExport={this.isSingleWidgetBoard() ? this.registerSoleExport : undefined}
                /* A chart registers one too, through buildNodePdfAttachment - so a board
                   holding a table and a chart sends ONE email with TWO PDFs, one per
                   widget, rather than the table alone. */
                onRegisterEmailAttachment={this.registerEmailAttachment(el.i ?? el.resourceId)}
                createdAt={this.props.board.createdAt}
                updatedAt={this.props.board.updatedAt}
                connectionData={el.reportData.connectionData}
                charts={el.chartConfig}
                resourceId={el.resourceId}
              />
            )}
          </>
        ) : (
          // fills the card and centres the mark in it - see .widget-pending
          <div className="widget-pending">
            <LoadingAnimation size="small" />
          </div>
        )}
      </>
    );
  }

  /* One grid cell. RGL writes its size and position inline from `data-grid`. It used to
     carry style={{ width: "100px !important" }}, which React drops as an invalid value
     and RGL overwrites anyway. */
  generateDOM(el) {
    return (
      <div key={`dc_${el.i}`} data-grid={el}>
        {this.renderWidgetBody(el)}
      </div>
    );
  }

  /* ── TELLING THE PAGE HEADER WE ARE BUSY ──
     The header's Download and Email buttons act on rows this component holds, and until
     they arrive there is nothing to write - Download produced an empty file and Email
     opened a dialog that could only fail on Send. Both are disabled while the overlay is
     up, which means the header has to know about a state that lives down here.

     Reported from componentDidUpdate rather than from render: calling back during a
     render would set state on the parent mid-render, which React warns about and which
     would re-enter this render. Only fired when the value actually changes, so an idle
     board is not dispatching on every update. */
  reportBusyState = () => {
    if (typeof this.props.onBusyChange !== "function") return;
    const busy = this.isLoadingWidgets() || this.state.isRefetching;
    if (busy === this.lastBusyState) return;
    this.lastBusyState = busy;
    this.props.onBusyChange(busy);
  };

  render() {
    const isLoading = this.isLoadingWidgets();
    const isReport =
      this.props.board && this.props.board.configDetails && this.props.board.configDetails.boardType === "reports";
    const isSingle = this.isSingleWidgetBoard();

    return (
      <div
        className={`report-widget board-widget-grid${isSingle ? " board-widget-grid--single" : ""}${
          isLoading ? " report-widget--loading" : ""
        }`}
      >
        {/* Overlay rather than hiding the grid: WidthProvider measures the container,
            and a hidden container measures 0 and lays the widgets out wrong.

            The spinner sits at the TOP of the overlay, not its centre. The overlay is as
            tall as the widget area, so on a board with several widgets "centred" put the
            spinner hundreds of pixels down the page - it looked like it moved further
            away the more widgets a board had. */}
        {isLoading || this.state.isRefetching ? (
          /* Two states, one overlay. A first load has nothing underneath so it is
             opaque; a refetch still has the previous rows under it, which are worth
             seeing through while the new ones arrive - hence the modifier. */
          <div className={`report-widget-loader${isLoading ? "" : " report-widget-loader--refetch"}`}>
            <div className="report-widget-loader-inner">
              <LoadingAnimation
                size="large"
                text={isLoading ? `Loading ${isReport ? "report" : "dashboard"}…` : "Updating…"}
              />
            </div>
          </div>
        ) : null}

        {/* Global filters for the whole board, above the widgets - see
            renderFilterBands for why they are not inside the widget card. */}
        {this.renderFilterBands()}

        {/* ── ONE WIDGET: NO GRID AT ALL ──
            A layout is a way of dividing space between widgets, and with one widget
            there is nothing to divide - so the saved x/y/w/h is ignored and the widget
            is handed the whole area, full width and full height (.board-widget-grid--single
            in dashboardStyles.css). Running it through RGL is what left the white space
            below: the grid is only ever as tall as the rows the widget was drawn at
            (a report's h:12 is 360px), however much page there is under it. */}
        {isSingle && this.state.layouts?.[0] ? (
          <div className="board-widget-single">{this.renderWidgetBody(this.state.layouts[0])}</div>
        ) : this.state.layouts ? (
          <ResponsiveReactGridLayout
            /* ── SAME GRID AS THE EDITOR CANVAS ──
               The spread has to come FIRST: defaultProps land in this.props, so spreading
               after an explicit prop silently overwrote it - which is how cols went back
               to md:6 / sm:6 even though 12 was passed in above it, and why a two-widget
               row wrapped into a stack here.

               One breakpoint at width 0 means "lg" always wins, and the editor edits a
               single saved layout (layouts.lg), so responsive breakpoints have nothing to
               offer - they only reinterpret that layout at a column count it was never
               designed for. compactType matches the editor, which saves its layout after
               vertical compaction. */
            {...this.props}
            breakpoints={{ lg: 0 }}
            cols={{ lg: 12 }}
            compactType="vertical"
            isDroppable={false}
            isResizable={false}
            isDraggable={false}
          >
            {_.map(this.state.layouts, (el, index) => this.generateDOM(el))}
          </ResponsiveReactGridLayout>
        ) : null}
      </div>
    );
  }
}

// const mapStateToProps = (state) => {
//     const { boards } = state;
//     return {
//         selectedBoard: boards.selectedBoard,
//     }
// }
// const mapDispatchToProps = (dispatch) => ({
//     getBoardByIdAsync: (data) => dispatch(getBoardByIdAsync(data)),
//     updateSelectedBoard: (data) => dispatch(updateSelectedBoard(data))
// })
// forwardRef so the board header can reach downloadAllWidgets() — the widget rows only
// exist inside this component.
export default connect(null, null, null, { forwardRef: true })(ReportWidjets);
